import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchBroadcastOptions, previewBroadcast, sendBroadcast, fetchBroadcastHistory } from '../api';
import './Broadcast.css';

//OPTION LISTS MIRROR OnboardingModal.jsx — SAME VALUES THE FILTERS MATCH AGAINST//
const GOALS = [
  { value: 'create-tech-packs', label: '🎨 Create tech packs' },
  { value: 'find-manufacturer', label: '🏭 Find a manufacturer' },
  { value: 'order-blanks', label: '👕 Order blanks' },
  { value: 'exploring', label: '🧭 Just exploring' }
];

const STAGES = [
  { value: 'idea', label: '💡 Just an idea' },
  { value: 'designs', label: '✏️ Have designs' },
  { value: 'sampling', label: '🧵 Sampling' },
  { value: 'selling', label: '🚀 Already selling' }
];

const CATEGORIES = ['Streetwear', 'Activewear', 'Cut & Sew', 'Knitwear', 'Denim', 'Outerwear', 'Accessories', 'Headwear', 'Other'];

const HEARD_FROM = [
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'google', label: 'Google' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'friend', label: 'A friend' },
  { value: 'ad', label: 'An ad' },
  { value: 'other', label: 'Other' }
];

const DESTINATIONS = [
  { value: 'notifications', label: 'Notifications' },
  { value: 'discover', label: 'Discover' },
  { value: 'blanks', label: 'Blanks Shop' },
  { value: 'my-orders', label: 'My Orders' },
  { value: 'projects', label: 'Projects' },
  { value: 'create', label: 'Create' },
  { value: 'find-manufacturer', label: 'Find a Manufacturer' },
  { value: 'memberships', label: 'Memberships' }
];

const TYPES = [
  { value: 'promo', label: 'Promo', defaultSubtitle: '🎉 New Deal' },
  { value: 'feature_announcement', label: 'Feature Announcement', defaultSubtitle: '✨ New on Tchpack' }
];

//iOS/ANDROID TRUNCATE PUSH BODIES AROUND HERE — WARN, DON'T BLOCK//
const BODY_WARN_LENGTH = 150;

const Broadcast = ({ session, onLogout }) => {
  const accessToken = session?.access_token;

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [type, setType] = useState('promo');
  const [destination, setDestination] = useState('notifications');

  const [segment, setSegment] = useState('all');
  const [goals, setGoals] = useState([]);
  const [stages, setStages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [heardFrom, setHeardFrom] = useState([]);
  const [countries, setCountries] = useState([]);
  const [smsOptedIn, setSmsOptedIn] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);

  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  //ONE CAMPAIGN ID PER COMPOSE — THE SERVER REJECTS A REPEAT, SO DOUBLE-CLICK CAN'T DOUBLE-SEND//
  const campaignIdRef = useRef(crypto.randomUUID());

  const buildAudience = useCallback(() => {
    if (segment !== 'custom') return { segment };
    const filters = {};
    if (goals.length) filters.signup_goal = goals;
    if (stages.length) filters.brand_stage = stages;
    if (categories.length) filters.product_categories = categories;
    if (heardFrom.length) filters.heard_from = heardFrom;
    if (countries.length) filters.country = countries;
    if (smsOptedIn) filters.sms_opted_in = true;
    return { segment, filters };
  }, [segment, goals, stages, categories, heardFrom, countries, smsOptedIn]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await fetchBroadcastHistory(accessToken);
      setHistory(data);
    } catch (err) {
      if (err.message === 'FORBIDDEN') { onLogout(); return; }
    } finally {
      setHistoryLoading(false);
    }
  }, [accessToken, onLogout]);

  useEffect(() => {
    loadHistory();
    fetchBroadcastOptions(accessToken)
      .then((data) => setCountryOptions(data.countries || []))
      .catch(() => {});
  }, [accessToken, loadHistory]);

  //LIVE REACH COUNT — DEBOUNCED SO CHIP-CLICKING DOESN'T SPAM THE API//
  useEffect(() => {
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await previewBroadcast(accessToken, buildAudience());
        setPreviewCount(data);
      } catch (err) {
        if (err.message === 'FORBIDDEN') { onLogout(); return; }
        setPreviewCount(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [accessToken, buildAudience, onLogout]);

  const toggleIn = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const activeType = TYPES.find((t) => t.value === type);
  const previewSubtitle = subtitle.trim() || activeType.defaultSubtitle;
  const canSend = title.trim() && message.trim() && (previewCount?.count || 0) > 0 && !sending;

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const result = await sendBroadcast(accessToken, {
        title: title.trim(),
        message: message.trim(),
        destination,
        type,
        payload: subtitle.trim() ? { subtitle: subtitle.trim() } : {},
        audience: buildAudience(),
        campaign_id: campaignIdRef.current,
        confirm: true
      });
      setConfirming(false);
      setSuccessMsg(
        `Sent to ${result.inserted} users` +
        (result.skipped_opted_out ? ` (${result.skipped_opted_out} opted out)` : '') +
        (result.push?.sent != null ? ` — ${result.push.sent} pushes delivered to devices.` : '.')
      );
      setTimeout(() => setSuccessMsg(''), 8000);
      setTitle('');
      setMessage('');
      setSubtitle('');
      campaignIdRef.current = crypto.randomUUID();
      loadHistory();
    } catch (err) {
      if (err.message === 'FORBIDDEN') { onLogout(); return; }
      setConfirming(false);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <>
      {successMsg && <div className="admin-success-banner">{successMsg}</div>}
      <main className="admin-main">
        {error && <div className="admin-error">{error}</div>}

        <div className="broadcast-grid">
          {/* COMPOSER */}
          <div className="broadcast-card">
            <h2 className="broadcast-card-title">Compose</h2>

            <label className="broadcast-label">Type</label>
            <div className="broadcast-chip-row">
              {TYPES.map((t) => (
                <button
                  key={t.value}
                  className={`broadcast-chip ${type === t.value ? 'selected' : ''}`}
                  onClick={() => setType(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <label className="broadcast-label">Title (bell headline)</label>
            <input
              className="broadcast-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="New drop 🎉"
              maxLength={80}
            />

            <label className="broadcast-label">
              Message
              <span className={`broadcast-counter ${message.length > BODY_WARN_LENGTH ? 'over' : ''}`}>
                {message.length}{message.length > BODY_WARN_LENGTH ? ` — may truncate past ${BODY_WARN_LENGTH}` : `/${BODY_WARN_LENGTH}`}
              </span>
            </label>
            <textarea
              className="broadcast-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="20% off all blanks orders this week only."
              rows={3}
              maxLength={500}
            />

            <div className="broadcast-field-row">
              <div className="broadcast-field">
                <label className="broadcast-label">Push subtitle (optional)</label>
                <input
                  className="broadcast-input"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder={activeType.defaultSubtitle}
                  maxLength={50}
                />
              </div>
              <div className="broadcast-field">
                <label className="broadcast-label">Tap destination</label>
                <select
                  className="broadcast-select"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                >
                  {DESTINATIONS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* PUSH PREVIEW */}
            <label className="broadcast-label">Push preview</label>
            <div className="broadcast-push-preview">
              <div className="broadcast-push-icon">T</div>
              <div className="broadcast-push-text">
                <div className="broadcast-push-title">Tchpack</div>
                <div className="broadcast-push-subtitle">{previewSubtitle}</div>
                <div className="broadcast-push-body">{message.trim() || "Check out what's new"}</div>
              </div>
              <div className="broadcast-push-time">now</div>
            </div>
          </div>

          {/* AUDIENCE */}
          <div className="broadcast-card">
            <h2 className="broadcast-card-title">Audience</h2>

            <div className="broadcast-chip-row">
              {[
                { value: 'all', label: 'All users' },
                { value: 'onboarded', label: 'Completed onboarding' },
                { value: 'custom', label: 'Custom' }
              ].map((s) => (
                <button
                  key={s.value}
                  className={`broadcast-chip ${segment === s.value ? 'selected' : ''}`}
                  onClick={() => setSegment(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {segment === 'custom' && (
              <div className="broadcast-filters">
                <label className="broadcast-label">Signup goal</label>
                <div className="broadcast-chip-row">
                  {GOALS.map((g) => (
                    <button key={g.value} className={`broadcast-chip small ${goals.includes(g.value) ? 'selected' : ''}`} onClick={() => toggleIn(goals, setGoals, g.value)}>{g.label}</button>
                  ))}
                </div>

                <label className="broadcast-label">Brand stage</label>
                <div className="broadcast-chip-row">
                  {STAGES.map((s) => (
                    <button key={s.value} className={`broadcast-chip small ${stages.includes(s.value) ? 'selected' : ''}`} onClick={() => toggleIn(stages, setStages, s.value)}>{s.label}</button>
                  ))}
                </div>

                <label className="broadcast-label">Product categories</label>
                <div className="broadcast-chip-row">
                  {CATEGORIES.map((c) => (
                    <button key={c} className={`broadcast-chip small ${categories.includes(c) ? 'selected' : ''}`} onClick={() => toggleIn(categories, setCategories, c)}>{c}</button>
                  ))}
                </div>

                <label className="broadcast-label">Heard about us from</label>
                <div className="broadcast-chip-row">
                  {HEARD_FROM.map((h) => (
                    <button key={h.value} className={`broadcast-chip small ${heardFrom.includes(h.value) ? 'selected' : ''}`} onClick={() => toggleIn(heardFrom, setHeardFrom, h.value)}>{h.label}</button>
                  ))}
                </div>

                {countryOptions.length > 0 && (
                  <>
                    <label className="broadcast-label">Country</label>
                    <div className="broadcast-chip-row">
                      {countryOptions.slice(0, 12).map((c) => (
                        <button key={c.value} className={`broadcast-chip small ${countries.includes(c.value) ? 'selected' : ''}`} onClick={() => toggleIn(countries, setCountries, c.value)}>{c.value} ({c.count})</button>
                      ))}
                    </div>
                  </>
                )}

                <label className="broadcast-checkbox-row">
                  <input type="checkbox" checked={smsOptedIn} onChange={(e) => setSmsOptedIn(e.target.checked)} />
                  Only users opted into SMS
                </label>
              </div>
            )}

            <div className="broadcast-reach">
              {previewLoading ? (
                <span className="broadcast-reach-loading">Counting…</span>
              ) : previewCount ? (
                <>
                  <span className="broadcast-reach-count">{previewCount.count.toLocaleString()}</span>
                  <span className="broadcast-reach-label">
                    users will receive this
                    {previewCount.skipped_opted_out > 0 && ` (${previewCount.skipped_opted_out} opted out of promos)`}
                  </span>
                </>
              ) : (
                <span className="broadcast-reach-loading">Reach unavailable</span>
              )}
            </div>

            <button
              className="broadcast-send-btn"
              disabled={!canSend}
              onClick={() => setConfirming(true)}
            >
              Send Broadcast
            </button>
          </div>
        </div>

        {/* HISTORY */}
        <div className="admin-table-wrapper broadcast-history">
          <div className="admin-table-header-bar">
            <h2>Past Broadcasts</h2>
          </div>
          {historyLoading ? (
            <div className="admin-loading">Loading…</div>
          ) : history.length === 0 ? (
            <div className="admin-empty"><h3>No broadcasts yet</h3><p>Sent campaigns will show up here with reach and engagement.</p></div>
          ) : (
            <table className="broadcast-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Sent</th>
                  <th>Seen</th>
                  <th>Tapped</th>
                </tr>
              </thead>
              <tbody>
                {history.map((b) => (
                  <tr key={b.id}>
                    <td>{formatDate(b.created_at)}</td>
                    <td className="broadcast-history-title">{b.title}<span className="broadcast-history-msg">{b.message}</span></td>
                    <td><span className={`admin-pill broadcast-pill-${b.type}`}>{b.type === 'promo' ? 'Promo' : 'Feature'}</span></td>
                    <td>{b.engagement?.sent?.toLocaleString()}</td>
                    <td>{b.engagement?.seen?.toLocaleString()}</td>
                    <td>{b.engagement?.tapped?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* CONFIRM MODAL */}
      {confirming && (
        <div className="broadcast-modal-overlay" onClick={() => !sending && setConfirming(false)}>
          <div className="broadcast-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Send to {previewCount?.count?.toLocaleString()} users?</h3>
            <p>This sends a push to every targeted device and adds a bell notification. It cannot be undone.</p>
            <div className="broadcast-modal-actions">
              <button className="broadcast-modal-cancel" onClick={() => setConfirming(false)} disabled={sending}>Cancel</button>
              <button className="broadcast-modal-confirm" onClick={handleSend} disabled={sending}>
                {sending ? 'Sending…' : 'Yes, send it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Broadcast;
